import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';

const InlineBatchEditorDialog = ({
    visible,
    items,
    onHide,
    onConfirm,
    availableItems
}) => {
    const [editedItems, setEditedItems] = useState([]);

    useEffect(() => {
        if (visible && items) {
            const initial = items.map(({ item, allocations }) => {
                let remainingQty = item.quantity;
                const updatedAllocations = [];

                for (const batch of allocations) {
                    if (remainingQty <= 0) {
                        updatedAllocations.push({ ...batch, allocated_quantity: 0 });
                        continue;
                    }
                    const allocQty = Math.min(batch.available_quantity, remainingQty);
                    remainingQty -= allocQty;
                    updatedAllocations.push({
                        ...batch,
                        allocated_quantity: allocQty
                    });
                }

                return {
                    originalItem: item,
                    included: true,
                    allocations: updatedAllocations
                };
            });
            setEditedItems(initial);
        }
    }, [visible, items]);

    const handleQtyChange = (itemIdx, batchIdx, value) => {
        setEditedItems(prev => {
            const updated = [...prev];
            updated[itemIdx].allocations[batchIdx].allocated_quantity = value;
            return updated;
        });
    };

    const handleIncludeToggle = (itemIdx) => {
        setEditedItems(prev => {
            const updated = [...prev];
            updated[itemIdx].included = !updated[itemIdx].included;
            return updated;
        });
    };

    const confirmHandler = () => {
        const finalItems = [];

        editedItems.forEach(({ originalItem, included, allocations }) => {
            if (!included) return;

            allocations.forEach(batch => {
                if (batch.allocated_quantity > 0) {
                    finalItems.push({
                        ...originalItem,
                        quantity: batch.allocated_quantity,
                        price: batch.selling_price,
                        batch_number: batch.batch_number,
                        expiry_date: batch.expiry_date,
                        price_list: batch.price_list_pk,
                        inventory: batch.inventory_id,
                    });
                }
            });
        });

        onConfirm([...availableItems, ...finalItems]);
    };

    return (
        <Dialog
            header="Batch Allocation Adjustment"
            visible={visible}
            onHide={onHide}
            modal
            style={{ width: '70vw' }}
            breakpoints={{ '960px': '95vw', '641px': '100vw' }}
        >
            {editedItems.map((itemWrapper, itemIdx) => (
                <div key={itemIdx} className="mb-4">
                    <div className="flex align-items-center justify-content-between">
                        <h5>{itemWrapper.originalItem.product?.name || 'Unnamed Product'}</h5>
                        <Checkbox
                            inputId={`include_${itemIdx}`}
                            checked={itemWrapper.included}
                            onChange={() => handleIncludeToggle(itemIdx)}
                        />
                        <label htmlFor={`include_${itemIdx}`}>Include in Invoice</label>
                    </div>
                    {itemWrapper.allocations.map((batch, batchIdx) => (
                        <div key={batchIdx} className="p-fluid grid formgrid">
                            <div className="field col-3">
                                <label>Batch</label>
                                <Tag value={batch.batch_number} severity="info" />
                            </div>
                            <div className="field col-3">
                                <label>Expiry</label>
                                <span>{batch.expiry_date}</span>
                            </div>
                            <div className="field col-2">
                                <label>Available</label>
                                <Tag value={batch.available_quantity} severity="success" />
                            </div>
                            <div className="field col-4">
                                <label>Allocate</label>
                                <InputNumber
                                    value={batch.allocated_quantity}
                                    min={0}
                                    max={batch.available_quantity}
                                    onValueChange={(e) => handleQtyChange(itemIdx, batchIdx, e.value)}
                                    showButtons
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
            <div className="flex justify-content-end mt-4">
                <Button label="Cancel" className="p-button-text mr-2" onClick={onHide} />
                <Button label="Confirm Invoice Items" icon="pi pi-check" onClick={confirmHandler} />
            </div>
        </Dialog>
    );
};

export default InlineBatchEditorDialog;
